import React, { useMemo, useState } from 'react';
import IconButton from '../../../../../components/IconButton';
import { TrashIcon } from '../../../../../components/IconButton/icons';
import { BalanceLeaveType, EmployeeLeave, LeaveAdjustment } from '../../../config';
import { accrualHours, countedYear, formatDay, formatDayShort, formatSignedHours } from './leaveFormat';
import { formatHoursShort } from '../../../../../utils/timesheetTime';

interface LeaveLedgerTableProps {
  leave: EmployeeLeave;
  leaveType: BalanceLeaveType;
  year: number;
  /** The opening balance, in the year it belongs to; 0 in every other year. */
  opening: number;
  canEdit: boolean;
  onDeleteAdjustment: (adjustment: LeaveAdjustment) => void;
}

interface LedgerRow {
  key: string;
  date: string;
  title: string;
  detail: string | null;
  hours: number;
  adjustment: LeaveAdjustment | null;
}

const COLLAPSED_ROWS = 8;

/**
 * Everything that has moved the balance other than leave being taken: the
 * opening balance, each pay period's accrual, and manual adjustments.
 *
 * The accrual rows are worked out by the database on every read, not stored,
 * so they cannot be edited here. A wrong accrual is fixed by correcting the
 * entitlement it was worked out from.
 */
const LeaveLedgerTable: React.FC<LeaveLedgerTableProps> = ({
  leave,
  leaveType,
  year,
  opening,
  canEdit,
  onDeleteAdjustment
}) => {
  const [showAll, setShowAll] = useState<boolean>(false);

  const rows = useMemo<LedgerRow[]>(() => {
    // A pay period across 1 January is two rows in the database, one in each
    // year, so within a year there is nothing to put back together: the part
    // of the period that fell in this year is the row.
    const accruals: LedgerRow[] = leave.accruals
      .filter((row) => row.leave_year === year)
      .map((row) => ({ row, hours: accrualHours(row, leaveType) }))
      .filter(({ hours }) => hours > 0)
      .map(({ row, hours }) => ({
        key: `accrual-${row.period_end}-${row.segment_start}`,
        // The last day the row covers, not the end of its pay period. They
        // are the same day unless the period was split, and then the period
        // end is wrong for the first part: the days of a period that fell in
        // 2025 were listed under 2025 with a January 2026 date.
        date: row.segment_end,
        title: `Pay period ${formatDayShort(row.period_start)} – ${formatDayShort(row.period_end)}`,
        detail: [
          `${formatHoursShort(Number(row.contracted_hours_per_week))} h/week`,
          row.days < 14 ? `${row.days} of 14 days` : null
        ].filter(Boolean).join(' · '),
        hours,
        adjustment: null
      }));

    const adjustments: LedgerRow[] = leave.adjustments
      .filter((adjustment) =>
        adjustment.leave_type === leaveType &&
        countedYear(adjustment.effective_date, leaveType, leave) === year
      )
      .map((adjustment) => ({
        key: `adjustment-${adjustment.id}`,
        date: adjustment.effective_date,
        title: adjustment.note,
        detail: adjustment.created_by_name ? `Adjustment by ${adjustment.created_by_name}` : 'Adjustment',
        hours: Number(adjustment.hours),
        adjustment
      }));

    const sorted = [...accruals, ...adjustments].sort((a, b) =>
      a.date < b.date ? 1 : a.date > b.date ? -1 : 0
    );

    if (opening !== 0 && leave.accrual_start) {
      sorted.push({
        key: 'opening',
        date: leave.accrual_start,
        title: 'Opening balance',
        detail: 'Carried in',
        hours: opening,
        adjustment: null
      });
    }
    return sorted;
  }, [leave, leaveType, year, opening]);

  if (rows.length === 0) {
    return (
      <p className="ui-table__empty">
        {year === leave.current_year
          ? `Nothing earned in ${year} yet. The pay period in progress ends ${formatDay(leave.next_period_end)}.`
          : `Nothing earned in ${year}.`}
      </p>
    );
  }

  const visible = showAll ? rows : rows.slice(0, COLLAPSED_ROWS);
  const earned = rows.reduce((sum, row) => sum + row.hours, 0);

  return (
    <>
      <table className="ui-table">
        <thead>
          <tr>
            <th className="col-date">Date</th>
            <th>Detail</th>
            <th className="is-num">Hours</th>
            <th className="ui-table__actions" aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {visible.map((row) => (
            <tr key={row.key}>
              <td>{formatDay(row.date)}</td>
              <td>
                <span className="leave-tab__ledger-title">{row.title}</span>
                {row.detail && <span className="leave-tab__ledger-detail">{row.detail}</span>}
              </td>
              <td className={`is-num ${row.hours < 0 ? 'leave-tab__negative' : ''}`.trim()}>
                {formatSignedHours(row.hours)}
              </td>
              <td className="ui-table__actions">
                {row.adjustment && canEdit && (
                  <span className="row-actions">
                    <IconButton
                      icon={<TrashIcon />}
                      label="Remove adjustment"
                      tone="danger"
                      size="sm"
                      onClick={() => row.adjustment && onDeleteAdjustment(row.adjustment)}
                    />
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="ui-table__totals">
          <tr>
            <td colSpan={2}>Earned and adjusted in {year}</td>
            <td className="is-num">{formatSignedHours(earned)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
      {rows.length > COLLAPSED_ROWS && (
        <div className="chip-bar">
          <button type="button" className="link-btn" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Show fewer' : `Show all ${rows.length}`}
          </button>
        </div>
      )}
    </>
  );
};

export default LeaveLedgerTable;
